import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixStoresCatalogV3Brand from './stores-catalog-v3-brand-brands-v-3.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A brand is a visible property of a product.
 * Adding brands to your products can help improve site and product visibility on search engines.
 */
export interface Brand {
  /**
   * Brand ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the brand is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the brand.
   *
   * Ignored when creating a brand.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the brand was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the brand was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Brand name.
   * >**Note:** `name` must be unique.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Number of products this brand is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Brand API requests.
   * @readonly
   */
  assignedProductsCount?: number | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateBrandRequest {
  /** Brand to create. */
  brand: Brand;
}

export interface CreateBrandResponse {
  /** Created brand. */
  brand?: Brand;
}

export interface GetBrandRequest {
  /**
   * Brand ID.
   * @format GUID
   */
  brandId: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  ASSIGNED_PRODUCTS_COUNT = 'ASSIGNED_PRODUCTS_COUNT',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'ASSIGNED_PRODUCTS_COUNT';

export interface GetBrandResponse {
  /** Brand. */
  brand?: Brand;
}

export interface UpdateBrandRequest {
  /** Brand to update. */
  brand: Brand;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateBrandResponse {
  /** Updated brand. */
  brand?: Brand;
}

export interface DeleteBrandRequest {
  /**
   * Brand ID.
   * @format GUID
   */
  brandId: string;
}

export interface DeleteBrandResponse {}

export interface QueryBrandsRequest {
  /** Query options. */
  query?: CursorQuery;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 300
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryBrandsResponse {
  /**
   * List of brands.
   * @minSize 1
   * @maxSize 300
   */
  brands?: Brand[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateBrandsRequest {
  /**
   * Brands to create.
   * @minSize 1
   * @maxSize 100
   */
  brands: Brand[];
  /**
   * Whether to return the full created brand entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateBrandsResponse {
  /**
   * Brands created by bulk action.
   * @maxSize 100
   */
  results?: BulkBrandsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkBrandsResult {
  /** Bulk action metadata for brand. */
  itemMetadata?: ItemMetadata;
  /**
   * Full brand entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  item?: Brand;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateBrandsRequest {
  /**
   * List of brands to update.
   * @minSize 1
   * @maxSize 100
   */
  brands: MaskedBrand[];
  /**
   * Whether to return the full updated brand entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedBrand {
  /** Brand to update. */
  brand?: Brand;
  /** Explicit list of fields to update. */
  fieldMask?: string[];
}

export interface BulkUpdateBrandsResponse {
  /**
   * Brands updated by bulk action.
   * @maxSize 100
   */
  results?: BulkBrandsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface GetOrCreateBrandRequest {
  /**
   * Brand name to retrieve or create.
   * @minLength 1
   * @maxLength 50
   */
  brandName: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface GetOrCreateBrandResponse {
  /** Brand. */
  brand?: Brand;
}

export interface BulkGetOrCreateBrandsRequest {
  /**
   * Brand names to retrieve or create.
   * @minLength 1
   * @maxLength 50
   * @minSize 1
   * @maxSize 100
   */
  brandNames: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkGetOrCreateBrandsResponse {
  /**
   * Brands retrieved or created by bulk action.
   * @maxSize 100
   */
  results?: BulkBrandsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkGetOrCreateBrandsForMigrationRequest {
  /**
   * Brand names to retrieve or create.
   * @minLength 1
   * @maxLength 50
   * @minSize 1
   * @maxSize 100
   */
  brandNames?: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkGetOrCreateBrandsForMigrationResponse {
  /**
   * Brands retrieved or created by bulk action.
   * @maxSize 100
   */
  results?: BulkBrandsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteBrandsRequest {
  /**
   * IDs of brands to delete.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  brandIds: string[];
}

export interface BulkDeleteBrandsResponse {
  /** Brands deleted by bulk action. */
  results?: BulkDeleteBrandsResponseBulkBrandsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteBrandsResponseBulkBrandsResult {
  /** Bulk action metadata for brand. */
  itemMetadata?: ItemMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface RecloneSiteRequest {
  /** @format GUID */
  cloneFrom?: string;
}

export interface RecloneSiteResponse {}

/**
 * Published on Greyhound topic "catalog-rollback-requested" after the site is handled by the V3 population rollback.
 * Each owning service reacts to this rollback request according to its own policy.
 */
export interface CatalogRollbackRequested {
  /**
   * The MetaSite ID of the store whose catalog migration rollback was requested.
   * @format GUID
   */
  metaSiteId?: string;
  /** Whether owner services should clean V3 catalog data as a reaction to this rollback request. */
  cleanData?: boolean;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateBrandValidationErrors =
  | {
      ruleName?: 'BRAND_LIMIT_EXCEEDED';
    }
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    };
/** @docsIgnore */
export type BulkCreateBrandsValidationErrors =
  | {
      ruleName?: 'BRAND_LIMIT_EXCEEDED';
    }
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    };
/** @docsIgnore */
export type GetOrCreateBrandValidationErrors = {
  ruleName?: 'BRAND_LIMIT_EXCEEDED';
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface BrandCreatedEnvelope {
  entity: Brand;
  metadata: EventMetadata;
}

/**
 * Triggered when a brand is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read brands in catalog v3 (PII)
 * @permissionScopeId SCOPE.STORES.BRAND_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Read brands in catalog v3
 * @permissionScopeId SCOPE.STORES.BRAND_READ
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.BRAND_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.brand_created
 * @serviceIdentifier com.wix.stores.catalog.brand.v3.BrandService
 * @slug created
 */
export declare function onBrandCreated(
  handler: (event: BrandCreatedEnvelope) => void | Promise<void>
): void;

export interface BrandDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a brand is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read brands in catalog v3 (PII)
 * @permissionScopeId SCOPE.STORES.BRAND_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Read brands in catalog v3
 * @permissionScopeId SCOPE.STORES.BRAND_READ
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.BRAND_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.brand_deleted
 * @serviceIdentifier com.wix.stores.catalog.brand.v3.BrandService
 * @slug deleted
 */
export declare function onBrandDeleted(
  handler: (event: BrandDeletedEnvelope) => void | Promise<void>
): void;

export interface BrandUpdatedEnvelope {
  entity: Brand;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a brand is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read brands in catalog v3 (PII)
 * @permissionScopeId SCOPE.STORES.BRAND_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Read brands in catalog v3
 * @permissionScopeId SCOPE.STORES.BRAND_READ
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.BRAND_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.brand_updated
 * @serviceIdentifier com.wix.stores.catalog.brand.v3.BrandService
 * @slug updated
 */
export declare function onBrandUpdated(
  handler: (event: BrandUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a brand.
 * @param brand - Brand to create.
 * @public
 * @requiredField brand
 * @requiredField brand.name
 * @permissionId WIX_STORES.BRAND_CREATE
 * @applicableIdentity APP
 * @returns Created brand.
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.CreateBrand
 */
export async function createBrand(
  brand: NonNullablePaths<Brand, `name`, 2>
): Promise<
  NonNullablePaths<Brand, `name`, 2> & {
    __validationErrorsType?: CreateBrandValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ brand: brand });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.createBrand(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.brand!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brand: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['brand']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a brand.
 * @param brandId - Brand ID.
 * @public
 * @requiredField brandId
 * @permissionId WIX_STORES.BRAND_READ
 * @applicableIdentity APP
 * @returns Brand.
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.GetBrand
 */
export async function getBrand(
  brandId: string,
  options?: GetBrandOptions
): Promise<NonNullablePaths<Brand, `name`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brandId: brandId,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.getBrand(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.brand!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brandId: '$[0]', fields: '$[1].fields' },
        singleArgumentUnchanged: false,
      },
      ['brandId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetBrandOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Updates a brand.
 *
 * Each time the brand is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the brand.
 * This ensures you're working with the latest brand and prevents unintended overwrites.
 * @param _id - Brand ID.
 * @public
 * @requiredField _id
 * @requiredField brand
 * @requiredField brand.revision
 * @permissionId WIX_STORES.BRAND_UPDATE
 * @applicableIdentity APP
 * @returns Updated brand.
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.UpdateBrand
 */
export async function updateBrand(
  _id: string,
  brand: NonNullablePaths<UpdateBrand, `revision`, 2>,
  options?: UpdateBrandOptions
): Promise<NonNullablePaths<Brand, `name`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brand: { ...brand, id: _id },
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.updateBrand(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.brand!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { brand: '$[1]' },
        explicitPathsToArguments: { 'brand.id': '$[0]', fields: '$[2].fields' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'brand', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateBrand {
  /**
   * Brand ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the brand is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the brand.
   *
   * Ignored when creating a brand.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the brand was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the brand was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Brand name.
   * >**Note:** `name` must be unique.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Number of products this brand is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Brand API requests.
   * @readonly
   */
  assignedProductsCount?: number | null;
}

export interface UpdateBrandOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Deletes a brand.
 *
 * > **Note:** Deleting a brand doesn't modify the products that reference it. The product's `brand.id` is left unchanged, becomes a dangling reference, and resolves to nothing on read which makes the product appear unbranded. To keep products on a different brand after deletion, reassign each product to the kept brand by calling [Update Product](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/update-product) (or [Bulk Update Products](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/bulk-update-products)) with `product.brand.id` set to the kept brand's `id` before calling Delete Brand. For the full reassign-then-delete pattern, see the [brands sample flows](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/sample-flows).
 * @param brandId - Brand ID.
 * @public
 * @requiredField brandId
 * @permissionId WIX_STORES.BRAND_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.DeleteBrand
 */
export async function deleteBrand(brandId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ brandId: brandId });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.deleteBrand(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brandId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['brandId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 100 brands, given the provided filtering, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 *
 * Query Brands runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 *
 * > **Note:** The set of filterable and sortable fields for Query Brands is narrower than the full Wix API Query Language, and the operators supported per field are also a subset. For the per-field matrix, see [Supported Filters and Sorting](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/supported-filters-and-sorting).
 *
 * > **Note:** `ASSIGNED_PRODUCTS_COUNT` is computed from the products search index and can lag recent Update Product writes. For workflows that depend on accurate brand-assignment counts immediately after a reassignment, see the [brands sample flows](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/sample-flows).
 * @public
 * @permissionId WIX_STORES.BRAND_READ
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.QueryBrands
 */
export function queryBrands(options?: QueryBrandsOptions): BrandsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Brand, 'CURSOR', QueryBrandsRequest, QueryBrandsResponse>(
    {
      func: async (payload: QueryBrandsRequest) => {
        const reqOpts = ambassadorWixStoresCatalogV3Brand.queryBrands({
          ...payload,
          ...(options ?? {}),
        });

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryBrandsRequest['query']) => {
        const args = [query, options] as [
          QueryBrandsRequest['query'],
          QueryBrandsOptions
        ];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryBrandsResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [])
        );

        return {
          items: transformedData?.brands,
          pagingMetadata: transformedData?.pagingMetadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'CURSOR',
      transformationPaths: {},
    }
  );
}

export interface QueryBrandsOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[] | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface BrandsQueryResult extends QueryCursorResult {
  items: Brand[];
  query: BrandsQueryBuilder;
  next: () => Promise<BrandsQueryResult>;
  prev: () => Promise<BrandsQueryResult>;
}

export interface BrandsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => BrandsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any[]
  ) => BrandsQueryBuilder;
  in: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => BrandsQueryBuilder;
  exists: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: boolean
  ) => BrandsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'_createdDate' | '_updatedDate' | 'name'>
  ) => BrandsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'_createdDate' | '_updatedDate' | 'name'>
  ) => BrandsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => BrandsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => BrandsQueryBuilder;
  find: () => Promise<BrandsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.QueryBrands
 * @requiredField query
 */
export async function typedQueryBrands(
  query: BrandQuery,
  options?: QueryBrandsOptions
): Promise<
  NonNullablePaths<QueryBrandsResponse, `brands` | `brands.${number}.name`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.queryBrands(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BrandQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_updatedDate', 'name'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['_id'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Brand, BrandQuerySpec>;
export type BrandQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 300 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Brand, BrandQuerySpec, BrandQuery>(),
  },
};

/**
 * Creates multiple brands.
 * @param brands - Brands to create.
 * @public
 * @requiredField brands
 * @requiredField brands.name
 * @permissionId WIX_STORES.BRAND_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.BulkCreateBrands
 */
export async function bulkCreateBrands(
  brands: NonNullablePaths<Brand, `name`, 2>[],
  options?: BulkCreateBrandsOptions
): Promise<
  NonNullablePaths<
    BulkCreateBrandsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __validationErrorsType?: BulkCreateBrandsValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brands: brands,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.bulkCreateBrands(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          brands: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['brands', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateBrandsOptions {
  /**
   * Whether to return the full created brand entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Updates multiple brands.
 *
 * Each time a brand is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating a brand.
 * This ensures you're working with the latest brand and prevents unintended overwrites.
 * @param brands - List of brands to update.
 * @public
 * @requiredField brands
 * @requiredField brands.brand._id
 * @requiredField brands.brand.revision
 * @permissionId WIX_STORES.BRAND_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.BulkUpdateBrands
 */
export async function bulkUpdateBrands(
  brands: NonNullablePaths<MaskedBrand, `brand._id` | `brand.revision`, 3>[],
  options?: BulkUpdateBrandsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateBrandsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brands: brands,
    returnEntity: options?.returnEntity,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.bulkUpdateBrands(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          brands: '$[0]',
          returnEntity: '$[1].returnEntity',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['brands', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateBrandsOptions {
  /**
   * Whether to return the full updated brand entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Retrieves a brand by name, or creates a brand if one with the passed `brandName` doesn't exist.
 * @param brandName - Brand name to retrieve or create.
 * @public
 * @requiredField brandName
 * @permissionId WIX_STORES.BRAND_GET_OR_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.GetOrCreateBrand
 */
export async function getOrCreateBrand(
  brandName: string,
  options?: GetOrCreateBrandOptions
): Promise<
  NonNullablePaths<GetOrCreateBrandResponse, `brand.name`, 3> & {
    __validationErrorsType?: GetOrCreateBrandValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brandName: brandName,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.getOrCreateBrand(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brandName: '$[0]', fields: '$[1].fields' },
        singleArgumentUnchanged: false,
      },
      ['brandName', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetOrCreateBrandOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Retrieves multiple brands by name, or creates multiple brands if those with the passed `brandNames` don't exist.
 *
 * > **Note:** `results.item` always contains the full brand entity for each retrieved or created brand. Bulk Get Or Create Brands doesn't accept a `returnEntity` parameter.
 * @param brandNames - Brand names to retrieve or create.
 * @public
 * @requiredField brandNames
 * @permissionId WIX_STORES.BRAND_GET_OR_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.BulkGetOrCreateBrands
 */
export async function bulkGetOrCreateBrands(
  brandNames: string[],
  options?: BulkGetOrCreateBrandsOptions
): Promise<
  NonNullablePaths<
    BulkGetOrCreateBrandsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    brandNames: brandNames,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Brand.bulkGetOrCreateBrands(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brandNames: '$[0]', fields: '$[1].fields' },
        singleArgumentUnchanged: false,
      },
      ['brandNames', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkGetOrCreateBrandsOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Deletes multiple brands.
 * @param brandIds - IDs of brands to delete.
 * @public
 * @requiredField brandIds
 * @permissionId WIX_STORES.BRAND_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.brand.v3.BrandService.BulkDeleteBrands
 */
export async function bulkDeleteBrands(
  brandIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteBrandsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ brandIds: brandIds });

  const reqOpts = ambassadorWixStoresCatalogV3Brand.bulkDeleteBrands(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { brandIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['brandIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
